import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { formatEstonianPhone } from '../utils/estonianFormatters';

const ContactSection = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactData, setContactData] = useState<any | null>(null);
  const API_BASE = useMemo(() => (import.meta as any)?.env?.VITE_API_URL || (typeof window !== 'undefined' && window.location && window.location.port === '5173' ? 'http://localhost:3002' : ''), []);
  
  // Get dynamic contact content from CMS
  const title = contactData?.title || t('contact.title') || 'Võta meiega ühendust';
  const subtitle = contactData?.subtitle || t('contact.subtitle') || 'Kui Sul on küsimusi või soovid meie teenuste kohta rohkem teada saada, siis võta meiega julgelt ühendust. Vastame kõigile päringutele 24 tunni jooksul.';
  const address = contactData?.address || t('contact.info.address') || 'Osmussaare tänav 8, 13811 Tallinn, Eesti';
  const phone1 = contactData?.phone1 || t('contact.info.phone1') || '+372 6707 110';
  const phone2 = contactData?.phone2 || t('contact.info.phone2') || '+372 5663 6076';
  const email = contactData?.email || t('contact.info.email') || 'info@forestryest.ee';
  const mapEmbedUrl = contactData?.map_embed_url || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2029.0023067891837!2d24.71488321611315!3d59.434270281697534!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4692956a0b41f889%3A0x3cf3696a2b4a93e1!2sOsmussaare%20t%C3%A4nav%208%2C%2013811%20Tallinn!5e0!3m2!1set!2see!4v1647523481278!5m2!1set!2see';

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/contact`);
        const json = await res.json();
        if (json?.ok && json?.data) setContactData(json.data);
      } catch {}
    })();
  }, [API_BASE]);

  const contactInfo = {
    address,
    phone: [phone1, phone2],
    email
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get form data
    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData);

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: (data as any).name,
          email: (data as any).email,
          phone: (data as any).phone,
          subject: (data as any).subject,
          message: (data as any).message,
        })
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending request:', error);
      alert(t('contact.form.errorMessage') || 'Viga sõnumi saatmisel. Palun proovige hiljem uuesti.');
      setIsSubmitting(false);
      return;
    }

    // Reset form
    (e.target as HTMLFormElement).reset();

    // Show success message
    alert(t('contact.form.successMessage') || 'Täname, et võtsite meiega ühendust! Vastame teile esimesel võimalusel.');

    setIsSubmitting(false);
  };

  return (
    <section id="contact" className="py-20 bg-gray-50 relative">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-forest-pattern opacity-10"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact info cards */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-xl shadow-md p-6 transition-all hover:shadow-lg">
              <div className="flex items-start">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('contact.address') || 'Aadress'}</h3>
                  <p className="text-gray-600">
                    <a href="#contact">{contactInfo.address}</a>
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 transition-all hover:shadow-lg">
              <div className="flex items-start">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('contact.phone') || 'Telefon'}</h3>
                  {contactInfo.phone.map((phone: string) => (
                    <p key={phone} className="text-gray-600">
                      <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-primary-700 transition-colors">
                        {formatEstonianPhone(phone)}
                      </a>
                    </p>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md p-6 transition-all hover:shadow-lg">
              <div className="flex items-start">
                <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                  <svg className="w-5 h-5 text-primary-700" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">{t('contact.email') || 'E-post'}</h3>
                  <p className="text-gray-600">
                    <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-primary-800 transition-colors">
                      {contactInfo.email}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-8">
              <h3 className="text-2xl font-heading font-bold text-primary-800 mb-6">
                {t('contact.sendMessage') || 'Saada meile sõnum'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.form.name') || 'Nimi'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder={t('contact.form.namePlaceholder') || 'Sisesta oma nimi'}
                        required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.form.email') || 'E-posti aadress'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder={t('contact.form.emailPlaceholder') || 'Sisesta oma e-posti aadress'}
                        required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      {'Telefon'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="phone"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder={'Sisesta oma telefon'}
                        required
                    />
                  </div>
                </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.form.subject') || 'Teema'} <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        id="subject"
                        name="subject"
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder={t('contact.form.subjectPlaceholder') || 'Sisesta sõnumi teema'}
                        required
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      {t('contact.form.message') || 'Sõnum'} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        id="message"
                        name="message"
                        rows={5}
                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder={t('contact.form.messagePlaceholder') || 'Kirjuta oma sõnum siia...'}
                        required
                    ></textarea>
                  </div>

                  <div>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="px-8 py-3 bg-primary-700 hover:bg-primary-800 text-white font-medium rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                    <span className="mr-2">
                      {isSubmitting ? (t('common.loading') || 'Laadimine...') : (t('contact.form.submit') || 'Saada sõnum')}
                    </span>
                      {!isSubmitting && (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                          </svg>
                      )}
                    </button>
                  </div>
              </form>
            </div>
          </div>
        </div>

        {/* Estonian Map */}
        <div className="mt-16 rounded-xl overflow-hidden shadow-lg">
          <iframe
              src={mapEmbedUrl}
              width="100%"
              height="400"
              style={{border: 0}}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
              title="Forestry OÜ asukoht Tallinnas"
          ></iframe>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;